// signPoll — the public sign page's FALLBACK status poll, made cheap.
//
// While the embedded DocuSeal form is showing (state 'ready'), the page re-polls
// GET /api/sign/:uuid so that, if DocuSeal's `completed` DOM event never fires,
// the webhook's status flip still advances the page to the thank-you. The poll
// used to run every 8 s for as long as the tab existed — hidden, backgrounded,
// or left open overnight — and every call is two keyed reads on the prod mirror
// cluster (136 calls/hour measured 2026-09-25), enough on its own to keep the
// cluster from ever going idle. Now:
//
//   • hidden tab → no polling at all; coming back → one immediate poll, then the
//     fast cadence again (the signer is back and may be about to sign);
//   • the cadence backs off the longer the page sits unchanged:
//       8 s for the first 2 min → 20 s until 10 min → 60 s until 30 min →
//       then it goes DORMANT (no polling): a visible page nobody touches for
//       30 min — left open overnight — must not keep the cluster awake;
//   • any interaction (pointer / key, or focus moving into or out of the
//     embedded form, whose own keystrokes the page cannot see) restarts the
//     fast cadence — from dormant with one immediate poll — so the moment
//     someone is actually signing the fallback is as quick as it ever was;
//   • the caller stops it entirely once the page leaves 'ready' (or the signer
//     completes locally) — terminal states never poll.
//
// Pure + injectable (timers, visibility, listeners) so it is unit-testable
// without a browser; SignPage wires it to `window` / `document`.

/** The cadence ladder: [until elapsed ms, delay ms]. Past the last rung the
 *  poll is dormant until the next interaction. */
export const SIGN_POLL_LADDER: ReadonlyArray<readonly [number, number]> = [
  [2 * 60_000, 8_000],
  [10 * 60_000, 20_000],
  [30 * 60_000, 60_000],
];

/** Delay before the next poll, given how long the page has sat unchanged;
 *  null = dormant (no poll until an interaction or a return to the tab). */
export function signPollDelay(elapsedMs: number): number | null {
  for (const [until, delay] of SIGN_POLL_LADDER) {
    if (elapsedMs < until) return delay;
  }
  return null;
}

export interface SignPollEnv {
  now: () => number;
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
  /** True when the page is hidden (document.visibilityState === 'hidden'). */
  isHidden: () => boolean;
  /** Subscribe to visibility changes; returns an unsubscribe. */
  onVisibilityChange: (fn: () => void) => () => void;
  /** Subscribe to user interaction (pointer/key); returns an unsubscribe. */
  onInteraction: (fn: () => void) => () => void;
}

/**
 * Start the fallback poll. `poll` is called on each tick (its errors are the
 * caller's to swallow). Returns a stop function; after stop, nothing fires.
 */
export function startSignPoll(poll: () => Promise<unknown> | void, env: SignPollEnv): () => void {
  let stopped = false;
  let timer: unknown = null;
  let since = env.now(); // start of the current "unchanged" stretch
  let inFlight = false;

  const clear = () => {
    if (timer !== null) env.clearTimeout(timer);
    timer = null;
  };

  const schedule = () => {
    clear();
    if (stopped || env.isHidden()) return; // hidden: wait for visibility
    const delay = signPollDelay(env.now() - since);
    if (delay === null) return; // dormant: wait for an interaction
    timer = env.setTimeout(tick, delay);
  };

  const runPoll = async () => {
    if (inFlight) return;
    inFlight = true;
    try {
      await poll();
    } catch {
      /* transient — keep polling on the ladder */
    } finally {
      inFlight = false;
    }
  };

  const tick = () => {
    timer = null;
    if (stopped) return;
    if (env.isHidden()) return; // went hidden between schedule and fire
    void runPoll().then(() => {
      if (!stopped) schedule();
    });
  };

  const offVisibility = env.onVisibilityChange(() => {
    if (stopped) return;
    if (env.isHidden()) {
      clear();
      return;
    }
    // Back on the page: catch up now, then restart the fast cadence.
    since = env.now();
    clear();
    void runPoll().then(() => {
      if (!stopped) schedule();
    });
  });

  const offInteraction = env.onInteraction(() => {
    if (stopped) return;
    const wasSlow = env.now() - since >= SIGN_POLL_LADDER[0]![0];
    since = env.now();
    if (timer === null && !inFlight && !env.isHidden()) {
      // Dormant: catch up now, then the fast cadence.
      void runPoll().then(() => {
        if (!stopped) schedule();
      });
      return;
    }
    // Only reschedule when we had backed off — a fast-cadence timer is already
    // as quick as a restart would be.
    if (wasSlow && timer !== null) schedule();
  });

  schedule();

  return () => {
    stopped = true;
    clear();
    offVisibility();
    offInteraction();
  };
}

/** The real browser environment. */
export function browserSignPollEnv(): SignPollEnv {
  return {
    now: () => Date.now(),
    setTimeout: (fn, ms) => window.setTimeout(fn, ms),
    clearTimeout: (h) => window.clearTimeout(h as number),
    isHidden: () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
    onVisibilityChange: (fn) => {
      document.addEventListener('visibilitychange', fn);
      return () => document.removeEventListener('visibilitychange', fn);
    },
    onInteraction: (fn) => {
      const opts: AddEventListenerOptions = { passive: true, capture: true };
      // focus/blur: the signer clicking into (blur) or out of (focus) the
      // embedded DocuSeal iframe — input inside it never reaches this window.
      const events = ['pointerdown', 'keydown', 'focus', 'blur'] as const;
      for (const e of events) window.addEventListener(e, fn, opts);
      return () => {
        for (const e of events) window.removeEventListener(e, fn, opts);
      };
    },
  };
}
